import { forwardRef } from "react";

import Slide from "@mui/material/Slide";
import Dialog from "@mui/material/Dialog";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { TransitionProps } from "@mui/material/transitions";
import Stack from "@mui/material/Stack";
import { useBoolean } from "src/hooks/use-boolean";
import { _applications } from "src/_mock";
import Iconify from "src/components/iconify";
import { t } from "i18next";

import {
  Tooltip,
  DialogContent,
  DialogTitle,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  SxProps,
  Theme,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import VideoPlayer from "src/components/videoplayer/videoplayer";
import { Trans } from "react-i18next";

import * as React from "react";
import { Box } from "@mui/material";

// ----------------------------------------------------------------------

const Transition = forwardRef(
  (
    props: TransitionProps & {
      children: React.ReactElement;
    },
    ref: React.Ref<unknown>
  ) => <Slide direction="up" ref={ref} {...props} />
);

type VideoObject = {
  title: string;
  src: string;
};
interface Section {
  key: string;
  title: string;
  content: string;
  video: VideoObject[];
}
interface HelpDialogProps {
  section?: string;
  tooltip?: string;
  trigger?: React.ReactNode; // <- custom clickable UI
  triggerSx?: SxProps<Theme>;
  sx?: SxProps<Theme>; // <- allow external style overrides
}

export default function PlayerHelpDialog({
  section,
  sx,
  trigger,
  triggerSx,
  tooltip,
}: HelpDialogProps) {
  const dialog = useBoolean();
  const theme = useTheme();

  // Define your sections
  const sections: Section[] = [
    {
      key: "player_help_section_start_steps",
      title: "player_help_section_start_steps",
      content: "player_help_section_start_steps_content",
      video: [], //"/assets/videos/about/foggo_promo_1_2022.mp4",
    },
    {
      key: "player_help_section_activities",
      title: "player_help_section_activities",
      content: "player_help_section_activities_content",
      video: [],
    },
    {
      key: "player_help_section_cancellation_info",
      title: "player_help_section_cancellation_info",
      content: "player_help_section_cancellation_content",
      video: [],
    },

    {
      key: "player_help_section_sportmates",
      title: "player_help_section_sportmates",
      content: "player_help_section_sportmates_content",
      video: [
        {
          title: "subscription",
          src: "/assets/videos/sportmates/sportmates_subscription_create_ad.mp4",
        },
        {
          title: "application",
          src: "/assets/videos/sportmates/sportmates_application.mp4",
        },
      ],
    },
    {
      key: "player_help_section_sportmates_item_details",
      title: "player_help_section_sportmates_item_details",
      content: "player_help_section_sportmates_item_details_content",
      video: [],
    },
    {
      key: "player_help_section_passestickets",
      title: "player_help_section_passestickets",
      content: "player_help_section_passestickets_content",
      video: [],
    },
    {
      key: "player_help_section_chat",
      title: "player_help_section_chat",
      content: "player_help_section_chat_content",
      video: [],
    },
    {
      key: "player_help_section_profile_data",
      title: "player_help_section_profile_data",
      content: "player_help_section_profile_data_content",
      video: [],
    },
    {
      key: "player_help_section_sportprovider_profile",
      title: "player_help_section_sportprovider_profile",
      content: "player_help_section_sportprovider_profile_content",
      video: [],
    },

    {
      key: "player_help_section_sportprovider_calendar",
      title: "player_help_section_sportprovider_calendar",
      content: "player_help_section_sportprovider_calendar_content",
      video: [],
    },
    {
      key: "player_help_section_sportprovider_trainings",
      title: "player_help_section_sportprovider_trainings",
      content: "player_help_section_sportprovider_trainings_content",
      video: [],
    },
    {
      key: "player_help_section_sportprovider_venues",
      title: "player_help_section_sportprovider_venues",
      content: "player_help_section_sportprovider_venues_content",
      video: [],
    },
    {
      key: "player_help_section_sportprovider_passes",
      title: "player_help_section_sportprovider_passes",
      content: "player_help_section_sportprovider_passes_content",
      video: [],
    },
    {
      key: "player_help_section_sportprovider_tickets",
      title: "player_help_section_sportprovider_tickets",
      content: "player_help_section_sportprovider_tickets_content",
      video: [],
    },
    {
      key: "player_help_section_sportprovider_rules",
      title: "player_help_section_sportprovider_rules",
      content: "player_help_section_sportprovider_rules_content",
      video: [],
    },
  ];

  // If a section prop is provided, reorder so that it appears first.
  const orderedSections = section
    ? [
        ...sections.filter((sec) => sec.key === section),
        ...sections.filter((sec) => sec.key !== section),
      ]
    : sections;

  const baseSx: SxProps<Theme> = {
    pt: 0.5,
    pr: 0.3,
    transform: "scale(1.3)",
    // transformOrigin: "top right",
    color: theme.palette.info.main,
  };

  // ✅ Merge safely (no undefined in the array)
  const mergedSx: SxProps<Theme> = Array.isArray(sx)
    ? [baseSx, ...sx]
    : sx
    ? [baseSx, sx]
    : baseSx;

  const title = tooltip ?? t("help");
  // If a custom trigger is provided, wrap it with a clickable container.
  // This avoids requiring the trigger to forward props, and keeps accessibility in one place.
  const TriggerNode = trigger ? (
    <Tooltip title={title}>
      <Box
        role="button"
        onClick={dialog.onTrue}
        sx={[
          {
            display: "inline-flex",
            alignItems: "center",
          },
          ...(Array.isArray(triggerSx)
            ? triggerSx
            : triggerSx
            ? [triggerSx]
            : []),
        ]}
      >
        {trigger}
      </Box>
    </Tooltip>
  ) : (
    <Tooltip title={title}>
      <IconButton onClick={dialog.onTrue} sx={mergedSx}>
        <Iconify icon="mingcute:question-line" />
      </IconButton>
    </Tooltip>
  );

  return (
    <>
      {TriggerNode}
      {/* <Tooltip title={t("help")}>
        <IconButton onClick={dialog.onTrue} sx={mergedSx}>
          <Iconify icon="mingcute:question-line" />
        </IconButton>
      </Tooltip> */}

      <Dialog
        fullScreen
        PaperProps={{
          sx: { maxWidth: 1000 },
        }}
        open={dialog.value}
        onClose={dialog.onFalse}
        TransitionComponent={Transition} // Ensure your Transition component is imported
      >
        <DialogTitle>
          <Toolbar>
            <Typography variant="h6" sx={{ flex: 1, ml: 0 }}>
              {t("help")}
            </Typography>
            <IconButton color="inherit" edge="end" onClick={dialog.onFalse}>
              <Iconify icon="mingcute:close-line" />
            </IconButton>
          </Toolbar>
        </DialogTitle>
        <DialogContent sx={{ overflowY: "auto" }}>
          <Stack spacing={2} mx={3} pb={3}>
            {orderedSections.map((sec) => (
              <Accordion
                key={sec.key}
                defaultExpanded={sec.key === section}
                sx={{
                  // When expanded, the root element of Accordion gets the "Mui-expanded" class.
                  // Here we override its background color to green.
                  "&.Mui-expanded": {
                    //backgroundColor: theme.palette.primary.main,
                    backgroundColor: theme.palette.secondary.main,
                    color: "white",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <Iconify
                      icon="ph:caret-down-bold"
                      width={20}
                      sx={{ float: "left" }}
                    />
                  }
                >
                  <Typography variant="subtitle1">{t(sec.title)}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack direction={"column"} spacing={2}>
                    <Stack>
                      {/* <Typography>{t(sec.content)}</Typography> */}
                      <Trans i18nKey={sec.content}></Trans>
                    </Stack>
                    {Array.isArray(sec.video) && sec.video.length > 0 && (
                      <Stack alignItems="center">
                        {sec.video.map((video, index) => (
                          <VideoPlayer
                            key={index}
                            src={t(video.src)}
                            alt={t(video.title)}
                          />
                        ))}
                      </Stack>
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}
